import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";

export default function CartDrawer() {
  const { drawerOpen, setDrawerOpen, items, updateQty, removeItem, subtotal, count } = useCart();
  const location = useLocation();

  // Auto-close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 ${
          drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setDrawerOpen(false)}
        data-testid="cart-drawer-backdrop"
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:max-w-md bg-[var(--mn-bg)] z-[70] transition-transform duration-500 ease-[cubic-bezier(0.2,0.7,0.2,1)] flex flex-col ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-6 py-6 border-b border-[var(--mn-border)]">
          <div>
            <p className="eyebrow">Your bag</p>
            <h2 className="font-display text-2xl mt-1">{count} item{count === 1 ? "" : "s"}</h2>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-2 hover:opacity-70"
            data-testid="cart-drawer-close"
            aria-label="Close cart"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20" data-testid="cart-empty">
              <ShoppingBag size={28} strokeWidth={1.2} className="text-[var(--mn-muted)]" />
              <p className="font-display text-2xl mt-6">Your bag is empty.</p>
              <p className="text-sm text-[var(--mn-ink-2)] mt-2 max-w-xs">
                Discover our curated edit of designer bags.
              </p>
              <Link
                to="/shop"
                onClick={() => setDrawerOpen(false)}
                className="btn-primary mt-8"
                data-testid="cart-empty-shop-cta"
              >
                Explore Collection
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--mn-border)]">
              {items.map((it) => (
                <li key={it.product_id} className="py-5 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                  <div className="w-20 h-24 bg-neutral-100 overflow-hidden shrink-0">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-display text-lg leading-tight">{it.name}</h4>
                    <p className="font-mono-luxe text-xs text-[var(--mn-ink-2)] mt-1">
                      {formatPrice(it.price)}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => updateQty(it.product_id, it.quantity - 1)}
                        className="w-8 h-8 border border-[var(--mn-border-strong)] flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
                        data-testid={`cart-decrement-${it.product_id}`}
                        aria-label="Decrease"
                      >
                        <Minus size={14} strokeWidth={1.5} />
                      </button>
                      <span className="font-mono-luxe text-sm w-8 text-center" data-testid={`cart-qty-${it.product_id}`}>
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => updateQty(it.product_id, it.quantity + 1)}
                        className="w-8 h-8 border border-[var(--mn-border-strong)] flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
                        data-testid={`cart-increment-${it.product_id}`}
                        aria-label="Increase"
                      >
                        <Plus size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => removeItem(it.product_id)}
                        className="ml-auto text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-accent)]"
                        data-testid={`cart-remove-${it.product_id}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="font-mono-luxe text-sm shrink-0">{formatPrice(it.price * it.quantity)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[var(--mn-border)] px-6 py-6 space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-[var(--mn-ink-2)]">Subtotal</span>
              <span className="font-mono-luxe" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
            </div>
            {subtotal > 0 && subtotal < 200 ? (
              <p className="text-xs text-[var(--mn-ink-2)]" data-testid="cart-shipping-hint">
                Add {formatPrice(200 - subtotal)} more for <span className="text-[var(--mn-ink)]">free shipping</span>.
              </p>
            ) : subtotal >= 200 ? (
              <p className="text-xs text-[var(--mn-accent)] uppercase tracking-[0.22em]" data-testid="cart-shipping-hint">
                ✓ Free shipping unlocked
              </p>
            ) : (
              <p className="text-xs text-[var(--mn-ink-2)]">Shipping & taxes calculated at checkout.</p>
            )}
            <Link
              to="/checkout"
              onClick={() => setDrawerOpen(false)}
              className="btn-primary w-full justify-center"
              data-testid="cart-checkout-cta"
            >
              Proceed to Checkout
            </Link>
            <Link
              to="/cart"
              onClick={() => setDrawerOpen(false)}
              className="block text-center text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)]"
              data-testid="cart-view-cart"
            >
              View full cart
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
